import { allOf, anyOf, signalEquals } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * The pest control follow-up: remind the resident of the service, ask about
 * pets and notes for the technician, then answer their questions from the
 * knowledge base. The agent cannot transfer the call, so a request for a
 * person goes to its own node, which takes a message for the team.
 */
const START_ID = '00000000-0000-4000-8000-00000004d001';
const GREET_ID = '00000000-0000-4000-8000-00000004a001';
const QUESTIONS_ID = '00000000-0000-4000-8000-00000004c001';
const MESSAGE_ID = '00000000-0000-4000-8000-00000004c002';
const END_CLOSE_ID = '00000000-0000-4000-8000-00000004a003';
const END_DNC_ID = '00000000-0000-4000-8000-00000004a005';
const edgeId = (n) => `00000000-0000-4000-8000-00000004e${String(n).padStart(3, '0')}`;
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: edgeId(1),
    exitEdgeId: edgeId(9),
    toNodeId: GREET_ID,
});
export const PEST_CONTROL_GRAPH_TEMPLATE = {
    name: 'Pest control follow-up',
    persona: 'You are Sam, a friendly assistant for Super. One or two short spoken ' +
        'sentences per turn.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [
        SCRATCHPAD_EVENTS.DO_NOT_CALL,
        SCRATCHPAD_EVENTS.HANDOFF_REQUESTED,
    ],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: "Hi {{firstName}}, this is Sam with Super. I'm reaching out with a " +
                'quick reminder that your pest control service is scheduled for ' +
                'next week. I just want to quickly collect two pieces of ' +
                "information that'll help our vendor's visit.",
        },
        {
            id: QUESTIONS_ID,
            type: 'conversation',
            title: 'Questions',
            position: 2,
            prompt: 'Open with the first question below, then ask the second, one at ' +
                'a time, and acknowledge each answer:\n\n' +
                '1. "Do you have any pets in the unit the technician should know ' +
                'about?"\n\n' +
                '2. "Is there anything else you\'d like the technician to know ' +
                'before they arrive?"\n\n' +
                'Then ask if they have any questions about the service, and answer ' +
                'from the knowledge base.',
            tools: [{ tool: 'SWITCH_LANGUAGE' }],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            speaksOnEntry: true,
            signals: [
                {
                    id: 'ready_to_wrap_up',
                    text: 'All three have happened: (1) the agent asked whether there are ' +
                        'pets in the unit, (2) the agent asked whether there is anything ' +
                        'the technician should know before the visit (a general ' +
                        '"anything else I can help with?" does not count), and (3) after ' +
                        'both, the caller said they have no other questions or are ready ' +
                        'to wrap up. Declining a question counts as answering it.',
                },
                {
                    id: 'needs_to_leave',
                    text: 'The caller says they need to end the call now, such as having to ' +
                        'go, being busy, or saying goodbye.',
                },
            ],
        },
        {
            id: MESSAGE_ID,
            type: 'conversation',
            title: 'Take a message',
            position: 3,
            prompt: 'Say you cannot transfer them right now, but you can take a ' +
                'message for the team to follow up. Take down their message and read it back briefly. Then ask: ' +
                '"Is there anything else I can help with?"',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            signals: [
                {
                    id: 'message_taken',
                    text: 'The caller gave their message for the team and said there is ' +
                        'nothing else',
                },
            ],
        },
        {
            id: END_CLOSE_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 4,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Thanks, I'll pass any notes along to the team. Have a great day!",
            },
        },
        {
            id: END_DNC_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update your contact so you don't get more calls " +
                    'from us. Have a good day.',
            },
        },
    ],
    edges: [
        OPENING.edges[0],
        // Cross-cutting flows, in priority order.
        {
            id: edgeId(3),
            fromNodeId: null,
            toNodeId: END_DNC_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.DO_NOT_CALL },
            position: 1,
        },
        {
            id: edgeId(4),
            fromNodeId: null,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 2,
        },
        { ...OPENING.edges[1], position: 3 },
        {
            id: edgeId(2),
            fromNodeId: GREET_ID,
            toNodeId: QUESTIONS_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 4,
        },
        {
            id: edgeId(5),
            fromNodeId: QUESTIONS_ID,
            toNodeId: END_CLOSE_ID,
            condition: anyOf(signalEquals(QUESTIONS_ID, 'ready_to_wrap_up', true), signalEquals(QUESTIONS_ID, 'needs_to_leave', true)),
            position: 5,
        },
        {
            id: edgeId(7),
            fromNodeId: MESSAGE_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(MESSAGE_ID, 'message_taken', true)),
            position: 6,
        },
    ],
};
//# sourceMappingURL=pest-control-template.js.map